import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Plus,
  Search,
  Wallet,
} from "lucide-react";
import {
  useAuth,
  useTenantBootstrap,
  useTenantWorkDashboard,
} from "@tark/shared/hooks";
import { stringingApi } from "@tark/shared/lib/stringing";
import {
  stringingBalance,
  stringingStatusLabel,
  stringingTotal,
  type StringingClient,
  type StringingDetail,
  type StringingInquiry,
  type StringingOrder,
  type StringingOrderInput,
  type StringingOverview,
  type StringingPage,
  type StringingServiceItem,
  type StringingStatus,
} from "@tark/shared/types/stringing";
import "./stringing.css";

type Scope = {
  tenantId: number;
  accountId: number;
  business: string;
  api: ReturnType<typeof stringingApi>;
};
const Context = createContext<Scope | null>(null);
const base = "/stringing";
function accountMismatch(error: unknown) {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { error?: string; errorCode?: string };
      };
    }
  )?.response;
  return (
    response?.status === 409 &&
    (response.data?.errorCode || response.data?.error) === "ACCOUNT_CHANGED"
  );
}
function useStringingScope() {
  const value = useContext(Context);
  if (!value) throw new Error("Stringing workspace is missing");
  return value;
}
function useRead<T>(path: string | null) {
  const s = useStringingScope(),
    queries = useQueryClient();
  const queryKey = ["stringing", s.accountId, s.tenantId, path];
  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        return await s.api<T>(path!, "GET", undefined, signal);
      } catch (error) {
        const response = (
          error as {
            response?: {
              status?: number;
              data?: { error?: string; errorCode?: string };
            };
          }
        ).response;
        const status = response?.status;
        const accountChanged = accountMismatch(error);
        if ([401, 403].includes(status || 0) || accountChanged) {
          queries.setQueriesData(
            { queryKey: ["stringing", s.accountId, s.tenantId] },
            null,
          );
        } else if (status === 404) {
          queries.setQueryData(queryKey, null);
        }
        throw error;
      }
    },
    enabled: path !== null,
    staleTime: 15000,
    retry: false,
  });
}
function useSave() {
  const s = useStringingScope(),
    queries = useQueryClient();
  return useMutation({
    mutationFn: ({
      path,
      body,
      method = "POST",
    }: {
      path: string;
      body: unknown;
      method?: string;
    }) => s.api(path, method, body),
    onSuccess: () => {
      void queries.invalidateQueries({
        queryKey: ["stringing", s.accountId, s.tenantId],
      });
    },
  });
}
function useRequestKey() {
  const last = useRef<{ payload: string; key: string }>();
  return (payload: unknown) => {
    const encoded = JSON.stringify(payload);
    if (last.current?.payload !== encoded)
      last.current = { payload: encoded, key: crypto.randomUUID() };
    return last.current!.key;
  };
}
function message(error: unknown) {
  const e = error as {
    response?: { status?: number; data?: { message?: string; error?: string } };
    message?: string;
  };
  if ((e.response?.status || 0) >= 500)
    return "Service unavailable. Please retry.";
  return (
    e.response?.data?.message ||
    e.response?.data?.error ||
    e.message ||
    "Unable to save. Please retry."
  );
}
const money = (amount: number, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
    amount,
  );
const when = (date: string, timezone?: string) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(date));
const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("");
function Avatar({ name }: { name: string }) {
  return (
    <span className="sg-avatar" aria-hidden="true">
      {initials(name)}
    </span>
  );
}
function Badge({ status }: { status: StringingStatus }) {
  return (
    <span className={`sg-badge sg-${status.toLowerCase()}`}>
      {stringingStatusLabel(status)}
    </span>
  );
}
function Head({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  const location = useLocation();
  return (
    <header className="sg-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <div className="sg-head-actions">
        {!location.pathname.endsWith("/inquiries") && (
          <Link
            className="sg-button"
            to={`${base}/inquiries`}
            aria-label="Listing inquiries"
          >
            Inquiries
          </Link>
        )}
        {action}
      </div>
    </header>
  );
}
function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="sg-panel">
      <div className="sg-panel-heading">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
function Back({ to, children }: { to: string; children: ReactNode }) {
  const location = useLocation();
  const requested = (location.state as { stringingReturnTo?: string } | null)
    ?.stringingReturnTo;
  const target =
    requested?.startsWith(`${base}/`) && !requested.includes("//")
      ? requested
      : to;
  return (
    <Link className="sg-back" to={target}>
      <ArrowLeft size={15} />
      {children}
    </Link>
  );
}
function Field({
  name,
  label,
  value,
  type = "text",
  required,
  placeholder,
  min,
  max,
  step,
  maxLength,
}: {
  name: string;
  label: string;
  value?: string | number;
  type?: string;
  required?: boolean;
  placeholder?: string;
  min?: string | number;
  max?: number;
  step?: string;
  maxLength?: number;
}) {
  return (
    <label className="sg-field">
      {label}
      <input
        name={name}
        type={type}
        defaultValue={value}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
      />
    </label>
  );
}
function QueryState({
  query,
  children,
}: {
  query: {
    isLoading: boolean;
    isError: boolean;
    data?: unknown;
    error: unknown;
    refetch: () => unknown;
  };
  children: ReactNode;
}) {
  const denied = [401, 403, 404].includes(
    (query.error as { response?: { status?: number } })?.response?.status || 0,
  );
  if (query.isLoading && query.data == null)
    return (
      <div className="sg-empty" role="status">
        Loading…
      </div>
    );
  if (query.isError && (query.data == null || denied))
    return (
      <div className="sg-empty" role="alert">
        <p>{message(query.error)}</p>
        <button className="sg-button" onClick={() => void query.refetch()}>
          Retry
        </button>
      </div>
    );
  return (
    <>
      {query.isError && (
        <div className="sg-empty" role="alert">
          <p>{message(query.error)}</p>
          <button className="sg-button" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      )}
      {children}
    </>
  );
}
function SaveError({ save }: { save: ReturnType<typeof useSave> }) {
  return save.isError ? (
    <p className="sg-error" role="alert">
      {message(save.error)}
    </p>
  ) : null;
}
function More({ hasMore, onMore }: { hasMore?: boolean; onMore: () => void }) {
  return hasMore ? (
    <button className="sg-button sg-more" onClick={onMore}>
      Next page <ArrowRight size={15} />
    </button>
  ) : null;
}
function Row({
  order,
  timezone,
}: {
  order: StringingOrder;
  timezone?: string;
}) {
  const location = useLocation();
  return (
    <Link
      className="sg-row sg-order-row"
      state={{ stringingReturnTo: location.pathname + location.search }}
      to={`${base}/orders/${order.id}`}
    >
      <span className="sg-grow">
        <strong>{order.clientName}</strong>
        <small className="sg-client-phone">
          {order.clientMobile || `Client #${order.clientId}`}
        </small>
        <small>
          {order.racquet} · #{order.id}
        </small>
      </span>
      <span className="sg-due">{when(order.dueAt, timezone)}</span>
      <span>
        <Badge status={order.status} />
      </span>
      <span className="sg-end">
        <strong>{money(stringingTotal(order), order.currency)}</strong>
        <small>
          {stringingBalance(order) > 0
            ? `${money(stringingBalance(order), order.currency)} unpaid`
            : "Paid"}
        </small>
      </span>
      <ChevronRight size={15} />
    </Link>
  );
}

/** Leaves non-stringer provider/commerce/coach routes intact. */
export function StringingRoute({
  children,
  view,
}: {
  children: ReactNode;
  view: string;
}) {
  const { user } = useAuth();
  const tenant = useTenantBootstrap(Boolean(user));
  const candidate =
    Boolean(tenant.activeTenant?.tenant?.id) &&
    tenant.activeTenant?.tenant?.tenantArchetype === "SERVICE_PROVIDER" &&
    ["OWNER", "ADMIN", "COACH", "STAFF"].includes(
      tenant.activeTenant?.roleCode?.toUpperCase() || "",
    );
  const work = useTenantWorkDashboard(Boolean(user) && candidate);
  if (candidate && work.isError)
    return (
      <div role="alert">
        Unable to load workspace.{" "}
        <button onClick={() => void work.refetch()}>Retry</button>
      </div>
    );
  if (candidate && work.isLoading)
    return <p role="status">Loading workspace…</p>;
  if (
    candidate &&
    work.data?.linkedProvider?.providerTypes.includes("STRINGER")
  )
    return <StringingWorkspace view={view} />;
  return <>{children}</>;
}
export default function StringingWorkspace({ view }: { view?: string }) {
  const { user } = useAuth();
  const { activeTenant, isSwitchingTenant } = useTenantBootstrap(Boolean(user));
  const tenant = activeTenant?.tenant,
    tenantId = tenant?.id,
    accountId = user?.id;
  if (isSwitchingTenant) return <p role="status">Switching workspace…</p>;
  if (
    !tenant ||
    !tenantId ||
    !accountId ||
    tenant.tenantArchetype !== "SERVICE_PROVIDER" ||
    !["OWNER", "ADMIN", "COACH", "STAFF"].includes(
      activeTenant!.roleCode.toUpperCase(),
    )
  )
    return <div className="sg-empty">Switch to your stringing workspace.</div>;
  return (
    <WorkspaceScope
      key={`${accountId}:${tenantId}`}
      tenantId={tenantId}
      accountId={accountId}
      business={tenant.displayName}
      view={view}
    />
  );
}
type AccessFailure = {
  status: number;
  accountChanged: boolean;
  retryFailed?: boolean;
};
function WorkspaceScope({
  tenantId,
  accountId,
  business,
  view,
}: {
  tenantId: number;
  accountId: number;
  business: string;
  view?: string;
}) {
  const queries = useQueryClient();
  const raw = useMemo(
    () => stringingApi(tenantId, accountId),
    [tenantId, accountId],
  );
  const prefix = useMemo(
    () => ["stringing", accountId, tenantId],
    [accountId, tenantId],
  );
  // Keep only a non-sensitive denial marker across route unmounts. Auth/workspace
  // changes already clear private queries, including this separately keyed marker.
  const accessKey = useMemo(
    () => ["stringing-access", accountId, tenantId],
    [accountId, tenantId],
  );
  const { data: accessError } = useQuery<AccessFailure | null>({
    queryKey: accessKey,
    queryFn: () => null,
    initialData: null,
    enabled: false,
  });
  const [retrying, setRetrying] = useState(false);
  const api = useMemo(
    () =>
      async function request<T>(
        path: string,
        method = "GET",
        data?: unknown,
        signal?: AbortSignal,
      ): Promise<T> {
        try {
          return await raw<T>(path, method, data, signal);
        } catch (error) {
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if ([401, 403].includes(status || 0) || accountMismatch(error)) {
            queries.setQueryData<AccessFailure>(accessKey, {
              status: status!,
              accountChanged: accountMismatch(error),
            });
            // Cancellation must not roll a pending read back to its pre-denial data.
            void queries.cancelQueries({ queryKey: prefix }, { revert: false });
            queries.setQueriesData({ queryKey: prefix }, null);
            // Null is an eviction placeholder, never a fresh successful resource.
            void queries.invalidateQueries({
              queryKey: prefix,
              refetchType: "none",
            });
          }
          throw error;
        }
      },
    [raw, queries, prefix, accessKey],
  );
  async function retryAccess() {
    setRetrying(true);
    try {
      await raw("/overview");
      void queries.cancelQueries({ queryKey: prefix }, { revert: false });
      queries.setQueriesData({ queryKey: prefix }, null);
      await queries.invalidateQueries({
        queryKey: prefix,
        refetchType: "none",
      });
      queries.setQueryData(accessKey, null);
    } catch {
      queries.setQueryData<AccessFailure | null>(accessKey, (old) =>
        old ? { ...old, retryFailed: true } : null,
      );
    } finally {
      setRetrying(false);
    }
  }
  return (
    <Context.Provider value={{ tenantId, accountId, business, api }}>
      {accessError ? (
        <div className="sg-workspace">
          <div className="sg-empty" role="alert">
            <p>
              {accessError.retryFailed
                ? "Service unavailable. Please retry."
                : accessError.accountChanged
                  ? "Your signed-in account changed. Reopen this workspace."
                  : accessError.status === 401
                    ? "Sign in again to reopen this workspace."
                    : "Workspace access removed. Please retry."}
            </p>
            {accessError.accountChanged || accessError.status === 401 ? (
              <button
                className="sg-button"
                onClick={() => window.location.reload()}
              >
                Reload workspace
              </button>
            ) : (
              <button
                className="sg-button"
                disabled={retrying}
                onClick={() => void retryAccess()}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      ) : (
        <WorkspaceBody view={view} />
      )}
    </Context.Provider>
  );
}

function WorkspaceBody({ view }: { view?: string }) {
  const location = useLocation(),
    parts = location.pathname.split("/").slice(2),
    section = view || parts[0] || "today";
  const id = parts[1] && /^\d+$/.test(parts[1]) ? Number(parts[1]) : null;
  return (
    <div className="sg-workspace">
      {section === "today" ? (
        <Today />
      ) : section === "orders" && parts[1] === "new" ? (
        <OrderForm key={location.search} />
      ) : section === "orders" && id ? (
        <Ticket key={id} id={id} />
      ) : section === "orders" ? (
        <Orders />
      ) : section === "clients" && parts[1] === "new" ? (
        <ClientForm key={location.search} />
      ) : section === "clients" && id ? (
        <ClientDetail key={id} id={id} />
      ) : section === "clients" ? (
        <Clients />
      ) : section === "services" ? (
        <Services />
      ) : section === "inquiries" ? (
        <Inquiries />
      ) : (
        <div className="sg-empty">
          Page unavailable. <Link to={base}>Open Today</Link>
        </div>
      )}
      {section !== "inquiries" && (
        <Link className="sg-mobile-inquiries" to={`${base}/inquiries`}>
          Inquiries
        </Link>
      )}
    </div>
  );
}
function Today() {
  const { business } = useStringingScope();
  const q = useRead<StringingOverview>("/overview"),
    data = q.data;
  return (
    <>
      <Head
        title="Today"
        subtitle={
          data
            ? new Intl.DateTimeFormat(undefined, {
                timeZone: data.timezone,
                weekday: "long",
                month: "long",
                day: "numeric",
              }).format(new Date())
            : business
        }
        action={
          <Link className="sg-button sg-primary" to={`${base}/orders/new`}>
            <Plus size={16} />
            New order
          </Link>
        }
      />
      <QueryState query={q}>
        {data && (
          <>
            <div className="sg-metrics">
              {[
                ["Active orders", data.activeOrders],
                ["Due today", data.dueToday],
                ["Ready for pickup", data.readyForPickup],
                ["Clients", data.clientCount],
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            <div className="sg-columns">
              <div className="sg-stack">
                {data.queue[0] && (
                  <section className="sg-feature">
                    <span>Next on the machine · #{data.queue[0].id}</span>
                    <h2>{data.queue[0].clientName}</h2>
                    <p>
                      {data.queue[0].racquet} · {data.queue[0].mainsTension} /{" "}
                      {data.queue[0].crossesTension}{" "}
                      {data.queue[0].tensionUnit.toLowerCase()}
                    </p>
                    <p>{when(data.queue[0].dueAt, data.timezone)}</p>
                    <Link
                      className="sg-button sg-primary"
                      to={`${base}/orders/${data.queue[0].id}`}
                    >
                      Open work ticket <ArrowRight size={15} />
                    </Link>
                  </section>
                )}
                <Panel
                  title="Work queue"
                  action={<Link to={`${base}/orders`}>All orders</Link>}
                >
                  {data.queue.length ? (
                    data.queue.map((order) => (
                      <Row
                        key={order.id}
                        order={order}
                        timezone={data.timezone}
                      />
                    ))
                  ) : (
                    <p className="sg-empty">No racquets waiting.</p>
                  )}
                </Panel>
              </div>
              <div className="sg-stack">
                <Panel title="Ready for pickup">
                  {data.ready.length ? (
                    data.ready.map((order) => (
                      <Link
                        className="sg-row"
                        key={order.id}
                        to={`${base}/orders/${order.id}`}
                      >
                        <Avatar name={order.clientName} />
                        <span className="sg-grow">
                          <strong>{order.clientName}</strong>
                          <small>{order.racquet}</small>
                        </span>
                        <span className="sg-end">
                          {stringingBalance(order)
                            ? money(stringingBalance(order), order.currency) +
                              " due"
                            : "Paid"}
                        </span>
                        <ChevronRight size={15} />
                      </Link>
                    ))
                  ) : (
                    <p className="sg-empty">No pickups waiting.</p>
                  )}
                </Panel>
                <Panel title="Payments">
                  <div className="sg-panel-body">
                    {data.money.length ? (
                      data.money.map((item) => (
                        <dl className="sg-receipt" key={item.currency}>
                          <div>
                            <dt>To collect · {item.currency}</dt>
                            <dd>{money(item.outstanding, item.currency)}</dd>
                          </div>
                          <div>
                            <dt>Received · {item.currency}</dt>
                            <dd>{money(item.received, item.currency)}</dd>
                          </div>
                        </dl>
                      ))
                    ) : (
                      <p className="sg-muted">No payments recorded yet.</p>
                    )}
                  </div>
                </Panel>
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}
function Orders() {
  const { business } = useStringingScope();
  const [listParams, setListParams] = useSearchParams();
  const filter = ["ACTIVE", "READY", "COLLECTED", "CANCELLED", "ALL"].includes(
    listParams.get("filter") || "",
  )
    ? listParams.get("filter")!
    : "ACTIVE";
  const search = listParams.get("search") || "";
  const cursor = /^\d+$/.test(listParams.get("cursor") || "")
    ? Number(listParams.get("cursor"))
    : null;
  const setFilter = (value: string) =>
    setListParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("filter", value);
        next.delete("cursor");
        return next;
      },
      { replace: true },
    );
  const setSearch = (value: string) =>
    setListParams(
      (current) => {
        const next = new URLSearchParams(current);
        value ? next.set("search", value) : next.delete("search");
        next.delete("cursor");
        return next;
      },
      { replace: true },
    );
  const setCursor = (value: number | null) =>
    setListParams(
      (current) => {
        const next = new URLSearchParams(current);
        value ? next.set("cursor", String(value)) : next.delete("cursor");
        return next;
      },
      { replace: true },
    );
  const q = useRead<StringingPage<StringingOrder>>(
      `/orders?${new URLSearchParams({ filter, search, ...(cursor ? { cursor: String(cursor) } : {}) })}`,
    ),
    overview = useRead<StringingOverview>("/overview");
  return (
    <>
      <Head
        title="Orders"
        subtitle={business}
        action={
          <Link className="sg-button sg-primary" to={`${base}/orders/new`}>
            <Plus size={16} />
            New order
          </Link>
        }
      />
      <div className="sg-toolbar">
        <div className="sg-tabs">
          {["ACTIVE", "READY", "COLLECTED", "CANCELLED", "ALL"].map((key) => (
            <button
              key={key}
              aria-pressed={key === filter}
              onClick={() => {
                setFilter(key);
                setCursor(null);
              }}
            >
              {key === "ACTIVE"
                ? "Active"
                : key === "ALL"
                  ? "All"
                  : stringingStatusLabel(key as StringingStatus)}
            </button>
          ))}
        </div>
        <label className="sg-search">
          <Search size={16} />
          <input
            aria-label="Find an order"
            placeholder="Player, racquet or mobile"
            value={search}
            maxLength={100}
            onChange={(e) => {
              setSearch(e.target.value);
              setCursor(null);
            }}
          />
        </label>
      </div>
      <QueryState query={q}>
        <div className="sg-panel">
          <div className="sg-table-head">
            <span>Player / racquet</span>
            <span>Due</span>
            <span>Status</span>
            <span>Amount</span>
            <span />
          </div>
          {q.data?.items.length ? (
            q.data.items.map((order) => (
              <Row
                key={order.id}
                order={order}
                timezone={overview.data?.timezone}
              />
            ))
          ) : (
            <p className="sg-empty">No matching orders.</p>
          )}
        </div>
        <div className="sg-actions">
          {cursor && (
            <button className="sg-button" onClick={() => setCursor(null)}>
              First page
            </button>
          )}
          <More
            hasMore={q.data?.hasMore}
            onMore={() => setCursor(q.data!.nextCursor)}
          />
        </div>
      </QueryState>
    </>
  );
}
function Clients() {
  const { business } = useStringingScope();
  const [search, setSearch] = useState(""),
    [cursor, setCursor] = useState<number | null>(null);
  const q = useRead<StringingPage<StringingClient>>(
    `/clients?${new URLSearchParams({ search, ...(cursor ? { cursor: String(cursor) } : {}) })}`,
  );
  return (
    <>
      <Head
        title="Clients"
        subtitle={business}
        action={
          <Link className="sg-button sg-primary" to={`${base}/clients/new`}>
            <Plus size={16} />
            Add client
          </Link>
        }
      />
      <label className="sg-search">
        <Search size={16} />
        <input
          value={search}
          aria-label="Name or mobile"
          placeholder="Name or mobile"
          maxLength={100}
          onChange={(e) => {
            setSearch(e.target.value);
            setCursor(null);
          }}
        />
      </label>
      <QueryState query={q}>
        <Panel title="Client directory">
          {q.data?.items.length ? (
            q.data.items.map((client) => (
              <Link
                className="sg-row"
                key={client.id}
                to={`${base}/clients/${client.id}`}
              >
                <Avatar name={client.name} />
                <span className="sg-grow">
                  <strong>{client.name}</strong>
                  <small className="sg-client-phone">
                    {client.mobile || "Mobile not provided"}
                  </small>
                </span>
                <span className="sg-end">
                  <strong>{client.orderCount}</strong>
                  <small>Orders</small>
                </span>
                <ChevronRight size={15} />
              </Link>
            ))
          ) : (
            <p className="sg-empty">No matching clients.</p>
          )}
        </Panel>
        <div className="sg-actions">
          {cursor && (
            <button className="sg-button" onClick={() => setCursor(null)}>
              First page
            </button>
          )}
          <More
            hasMore={q.data?.hasMore}
            onMore={() => setCursor(q.data!.nextCursor)}
          />
        </div>
      </QueryState>
    </>
  );
}
function SourceInquiry({ params }: { params: URLSearchParams }) {
  const id = /^\d+$/.test(params.get("sourceRequestId") || "")
    ? Number(params.get("sourceRequestId"))
    : null;
  const cursor = /^\d+$/.test(params.get("sourceCursor") || "")
    ? `?cursor=${params.get("sourceCursor")}`
    : "";
  const q = useRead<StringingPage<StringingInquiry>>(
    id ? `/inquiries${cursor}` : null,
  );
  const inquiry = q.data?.items.find((item) => item.id === id);
  if (!id) return null;
  return (
    <section className="sg-inquiry-context" aria-label="Source inquiry">
      <span className="sg-kicker">From inquiry #{id}</span>
      {inquiry ? (
        <>
          <strong>{inquiry.name}</strong>
          <span>
            {inquiry.email || inquiry.mobile || inquiry.contactMethod}
          </span>
          {inquiry.message ? <p>{inquiry.message}</p> : null}
        </>
      ) : (
        <p role={q.isError ? "alert" : "status"}>
          {q.isLoading
            ? "Loading inquiry…"
            : "Inquiry unavailable. Return to Inquiries to check the contact."}
        </p>
      )}
    </section>
  );
}
function ClientForm() {
  const navigate = useNavigate(),
    save = useSave(),
    key = useRequestKey(),
    [params] = useSearchParams();
  const linkedUser = /^\d+$/.test(params.get("userId") || "")
    ? Number(params.get("userId"))
    : null;
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const input = {
      name: String(form.get("name")).trim(),
      mobile: String(form.get("mobile")).trim() || null,
      notes: String(form.get("notes")).trim() || null,
      userId: linkedUser,
    };
    try {
      const data = (await save.mutateAsync({
        path: "/clients",
        body: { ...input, requestId: key(input) },
      })) as StringingClient;
      const source = params.get("sourceRequestId");
      navigate(
        `${base}/orders/new?clientId=${data.id}${source && /^\d+$/.test(source) ? `&sourceRequestId=${source}${/^\d+$/.test(params.get("sourceCursor") || "") ? `&sourceCursor=${params.get("sourceCursor")}` : ""}` : ""}`,
        { replace: true },
      );
    } catch {
      /* Retain form and retry key. */
    }
  }
  return (
    <>
      <Back to={`${base}/clients`}>Clients</Back>
      <Head title="Add client" />
      <SourceInquiry params={params} />
      {params.get("sourceRequestId") ? (
        <p className="sg-handoff">
          Choose an existing client or add this contact before creating the
          order.{" "}
          <Link
            to={`${base}/orders/new?sourceRequestId=${encodeURIComponent(params.get("sourceRequestId")!)}${params.get("sourceCursor") ? `&sourceCursor=${encodeURIComponent(params.get("sourceCursor")!)}` : ""}`}
          >
            Choose existing client
          </Link>
        </p>
      ) : null}
      <form onSubmit={(e) => void submit(e)}>
        <Panel title="Contact">
          <div className="sg-panel-body sg-fields">
            <Field
              name="name"
              label="Name *"
              value={params.get("name") || ""}
              required
              maxLength={120}
            />
            <Field
              name="mobile"
              label="Mobile"
              type="tel"
              maxLength={25}
              placeholder="Country code + mobile"
            />
            <label className="sg-field sg-full">
              Notes
              <textarea name="notes" maxLength={2000} />
            </label>
          </div>
        </Panel>
        <SaveError save={save} />
        <div className="sg-form-actions">
          <Link className="sg-button" to={`${base}/clients`}>
            Cancel
          </Link>
          <button className="sg-button sg-primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save client"}
          </button>
        </div>
      </form>
    </>
  );
}
function ClientDetail({ id }: { id: number }) {
  const q = useRead<StringingClient>(`/clients/${id}`),
    [cursor, setCursor] = useState<number | null>(null);
  const orders = useRead<StringingPage<StringingOrder>>(
      `/orders?filter=ALL&clientId=${id}${cursor ? `&cursor=${cursor}` : ""}`,
    ),
    overview = useRead<StringingOverview>("/overview");
  return (
    <>
      <Back to={`${base}/clients`}>Clients</Back>
      <QueryState query={q}>
        {q.data && (
          <>
            <Head
              title={q.data.name}
              subtitle={q.data.mobile || "Mobile not provided"}
              action={
                <Link
                  className="sg-button sg-primary"
                  to={`${base}/orders/new?clientId=${id}`}
                >
                  <Plus size={15} />
                  New order
                </Link>
              }
            />
            <div className="sg-columns">
              <QueryState query={orders}>
                <Panel title={`Order history · ${q.data.orderCount}`}>
                  {orders.data?.items.length ? (
                    orders.data.items.map((order) => (
                      <Row
                        key={order.id}
                        order={order}
                        timezone={overview.data?.timezone}
                      />
                    ))
                  ) : (
                    <p className="sg-empty">No orders yet.</p>
                  )}
                </Panel>
                <More
                  hasMore={orders.data?.hasMore}
                  onMore={() => setCursor(orders.data!.nextCursor)}
                />
              </QueryState>
              <Panel title="Client notes">
                <p className="sg-panel-body sg-muted">
                  {q.data.notes || "No notes yet."}
                </p>
              </Panel>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}
function OrderForm() {
  const navigate = useNavigate(),
    save = useSave(),
    key = useRequestKey(),
    [params] = useSearchParams();
  const summaryRef = useRef<HTMLDivElement>(null);
  const intakeForm = useRef<HTMLFormElement>(null);
  const updateSetupReview = () => {
    if (!intakeForm.current) return;
    const data = new FormData(intakeForm.current);
    setSetupReview(
      Object.fromEntries(
        [
          "racquet",
          "mainsString",
          "crossesString",
          "mainsTension",
          "crossesTension",
          "unit",
          "dueAt",
        ].map((key) => [key, String(data.get(key) || "")]),
      ),
    );
  };
  const [setupReview, setSetupReview] = useState<Record<string, string>>({});
  const [clientId, setClientId] = useState(params.get("clientId") || ""),
    [clientSearch, setClientSearch] = useState(""),
    [serviceId, setServiceId] = useState(""),
    [supplied, setSupplied] = useState(false),
    [price, setPrice] = useState({
      labor: "20",
      materials: "0",
      currency: "USD",
    }),
    [localError, setLocalError] = useState("");
  const clients = useRead<StringingPage<StringingClient>>(
      `/clients?search=${encodeURIComponent(clientSearch)}&size=50`,
    ),
    selected = useRead<StringingClient>(
      clientId ? `/clients/${clientId}` : null,
    ),
    services = useRead<StringingServiceItem[]>("/services"),
    overview = useRead<StringingOverview>("/overview");
  const options = [...(clients.data?.items || [])];
  if (selected.data && !options.some((c) => c.id === selected.data?.id))
    options.unshift(selected.data);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLocalError("");
    const f = new FormData(e.currentTarget);
    const due = new Date(String(f.get("dueAt")));
    if (!clientId || !Number.isFinite(due.valueOf())) {
      setLocalError("Select a client and pickup date.");
      return;
    }
    const input: Omit<StringingOrderInput, "requestId"> = {
      clientId: Number(clientId),
      serviceId: serviceId ? Number(serviceId) : null,
      sourceRequestId: /^\d+$/.test(params.get("sourceRequestId") || "")
        ? Number(params.get("sourceRequestId"))
        : null,
      racquet: String(f.get("racquet")).trim(),
      mainsString: String(f.get("mainsString")).trim(),
      crossesString: String(f.get("crossesString")).trim(),
      mainsTension: Number(f.get("mainsTension")),
      crossesTension: Number(f.get("crossesTension")),
      tensionUnit: f.get("unit") as "LB" | "KG",
      playerSuppliesStrings: supplied,
      dueAt: due.toISOString(),
      laborAmount: Number(price.labor),
      materialsAmount: supplied ? 0 : Number(price.materials),
      currency: price.currency,
      notes: String(f.get("notes")).trim() || null,
      assignedUserId: null,
    };
    try {
      const data = (await save.mutateAsync({
        path: "/orders",
        body: { ...input, requestId: key(input) },
      })) as StringingDetail;
      navigate(`${base}/orders/${data.order.id}`, { replace: true });
    } catch {
      /* Preserve entered setup. */
    }
  }
  return (
    <>
      <Back to={`${base}/orders`}>Orders</Back>
      <Head title="New order" subtitle="One racquet · One order" />
      <SourceInquiry params={params} />
      <form
        onSubmit={(e) => void submit(e)}
        ref={intakeForm}
        onInputCapture={updateSetupReview}
        onChangeCapture={updateSetupReview}
        className="sg-intake-form"
      >
        <div className="sg-columns">
          <div className="sg-stack">
            <Panel title="1 · Client & racquet">
              <div className="sg-panel-body sg-fields">
                <label className="sg-field sg-full">
                  Find client
                  <input
                    aria-label="Find client"
                    value={clientSearch}
                    maxLength={100}
                    placeholder="Name or mobile"
                    onChange={(e) => setClientSearch(e.target.value)}
                  />
                </label>
                <label className="sg-field sg-full">
                  Client *
                  <select
                    required
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  >
                    <option value="">Choose client</option>
                    {options.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.mobile || "Mobile not provided"}
                      </option>
                    ))}
                  </select>
                  <Link to={`${base}/clients/new`}>Add a client</Link>
                  {clients.isError && (
                    <span role="alert">{message(clients.error)}</span>
                  )}
                </label>
                <Field
                  name="racquet"
                  label="Racquet *"
                  required
                  maxLength={160}
                  placeholder="Yonex EZONE 98"
                />
                <label className="sg-field">
                  Service
                  <select
                    value={serviceId}
                    onChange={(e) => {
                      setServiceId(e.target.value);
                      const s = services.data?.find(
                        (x) => x.id === Number(e.target.value),
                      );
                      if (s)
                        setPrice({
                          labor: String(s.laborAmount),
                          materials: String(s.materialsAmount),
                          currency: s.currency,
                        });
                    }}
                  >
                    <option value="">Custom setup</option>
                    {services.data
                      ?.filter((s) => s.active)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </select>
                </label>
                <p className="sg-caption sg-muted sg-full">
                  Choosing a service applies its price defaults. Adjust the
                  quote as needed.
                </p>
                <Field
                  name="mainsString"
                  label="Mains string *"
                  required
                  maxLength={160}
                />
                <Field
                  name="crossesString"
                  label="Crosses string *"
                  required
                  maxLength={160}
                />
                <Field
                  name="mainsTension"
                  label="Mains tension *"
                  type="number"
                  required
                  min={1}
                  max={90}
                  step="0.5"
                />
                <Field
                  name="crossesTension"
                  label="Crosses tension *"
                  type="number"
                  required
                  min={1}
                  max={90}
                  step="0.5"
                />
                <label className="sg-field">
                  Unit
                  <select name="unit">
                    <option value="LB">lb</option>
                    <option value="KG">kg</option>
                  </select>
                </label>
                <label className="sg-check">
                  <input
                    type="checkbox"
                    checked={supplied}
                    onChange={(e) => setSupplied(e.target.checked)}
                  />
                  Player supplies strings
                </label>
                <label className="sg-field sg-full">
                  Notes
                  <textarea name="notes" maxLength={2000} />
                </label>
              </div>
            </Panel>
            <Panel title="2 · Pickup">
              <div className="sg-panel-body">
                <Field
                  name="dueAt"
                  label="Pickup date & time *"
                  required
                  type="datetime-local"
                />
                <p className="sg-muted sg-caption">
                  Entered in your device timezone (
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}). Studio:{" "}
                  {overview.data?.timezone || "Loading…"}
                </p>
              </div>
            </Panel>
          </div>
          <Panel title="3 · Quote">
            <div className="sg-panel-body sg-fields">
              <label className="sg-field">
                Labor fee · {price.currency}
                <input
                  type="number"
                  min="0"
                  max="99999999.99"
                  step="0.01"
                  required
                  value={price.labor}
                  onChange={(e) =>
                    setPrice({ ...price, labor: e.target.value })
                  }
                />
              </label>
              <label className="sg-field">
                String materials · {price.currency}
                <input
                  type="number"
                  min="0"
                  max="99999999.99"
                  step="0.01"
                  required
                  disabled={supplied}
                  value={supplied ? "0" : price.materials}
                  onChange={(e) =>
                    setPrice({ ...price, materials: e.target.value })
                  }
                />
              </label>
              <label className="sg-field sg-full">
                Currency
                <input
                  required
                  pattern="[A-Z]{3}"
                  maxLength={3}
                  value={price.currency}
                  onChange={(e) =>
                    setPrice({
                      ...price,
                      currency: e.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <div className="sg-receipt sg-full">
                <div className="sg-total">
                  <span>Total</span>
                  <strong>
                    {/^[A-Z]{3}$/.test(price.currency)
                      ? safeMoney(
                          Number(price.labor || 0) +
                            (supplied ? 0 : Number(price.materials || 0)),
                          price.currency,
                        )
                      : "—"}
                  </strong>
                </div>
              </div>
            </div>
          </Panel>
        </div>
        <div
          ref={summaryRef}
          className="sg-intake-review"
          role="region"
          aria-label="Order summary"
        >
          <Panel title="Review order">
            <dl className="sg-panel-body sg-review-grid">
              <div>
                <dt>Client / racquet</dt>
                <dd>
                  {selected.data?.name || "Choose a client"} ·{" "}
                  {setupReview.racquet || "Enter racquet"}
                </dd>
              </div>
              <div>
                <dt>Mains</dt>
                <dd>
                  {setupReview.mainsString || "—"} ·{" "}
                  {setupReview.mainsTension || "—"}{" "}
                  {(setupReview.unit || "LB").toLowerCase()}
                </dd>
              </div>
              <div>
                <dt>Crosses</dt>
                <dd>
                  {setupReview.crossesString || "—"} ·{" "}
                  {setupReview.crossesTension || "—"}{" "}
                  {(setupReview.unit || "LB").toLowerCase()}
                </dd>
              </div>
              <div>
                <dt>Pickup</dt>
                <dd>
                  {setupReview.dueAt &&
                  Number.isFinite(new Date(setupReview.dueAt).valueOf())
                    ? when(
                        new Date(setupReview.dueAt).toISOString(),
                        overview.data?.timezone,
                      )
                    : "Choose pickup"}
                </dd>
              </div>
              <div>
                <dt>Strings from</dt>
                <dd>{supplied ? "Player" : "Studio"}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>
                  {safeMoney(
                    Number(price.labor || 0) +
                      (supplied ? 0 : Number(price.materials || 0)),
                    price.currency,
                  )}
                </dd>
              </div>
            </dl>
          </Panel>
        </div>
        {localError && (
          <p className="sg-error" role="alert">
            {localError}
          </p>
        )}
        <SaveError save={save} />
        <div className="sg-form-actions sg-form-dock">
          <div className="sg-grow">
            <strong>{selected.data?.name || "Choose a client"}</strong>
            <small>
              {selected.data?.mobile ||
                (clientId ? `Client #${clientId}` : "One racquet · One order")}
            </small>
          </div>
          <strong className="sg-quote-total">
            {safeMoney(
              Number(price.labor || 0) +
                (supplied ? 0 : Number(price.materials || 0)),
              price.currency,
            )}
          </strong>
          <Link className="sg-button" to={`${base}/orders`}>
            Cancel
          </Link>
          <button
            type="button"
            className="sg-button"
            onClick={() => {
              updateSetupReview();
              summaryRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }}
          >
            Review setup
          </button>
          <button className="sg-button sg-primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Create order"}
          </button>
        </div>
      </form>
    </>
  );
}
function studioDate(timezone?: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  return ["year", "month", "day"]
    .map((type) => parts.find((p) => p.type === type)!.value)
    .join("-");
}
function safeMoney(amount: number, currency: string) {
  try {
    return money(amount, currency);
  } catch {
    return amount.toFixed(2);
  }
}
function TicketNotes({
  order,
  onSave,
  busy,
}: {
  order: StringingOrder;
  busy: boolean;
  onSave: (version: number, notes: string) => void;
}) {
  const [draft, setDraft] = useState<{
    value: string;
    base: string;
    version: number;
  } | null>(null);
  const saved = order.notes || "";
  useEffect(() => {
    if (draft && draft.value === saved && order.version > draft.version)
      setDraft(null);
  }, [draft, saved, order.version]);
  const conflict = Boolean(
    draft && draft.base !== saved && draft.value !== saved,
  );
  return (
    <Panel title="Notes">
      <form
        className="sg-panel-body"
        onSubmit={(e) => {
          e.preventDefault();
          if (!conflict) onSave(order.version, draft?.value ?? saved);
        }}
      >
        <label className="sg-field">
          Setup notes
          <textarea
            name="notes"
            value={draft?.value ?? saved}
            maxLength={2000}
            onChange={(e) =>
              setDraft((current) => ({
                value: e.target.value,
                base: current?.base ?? saved,
                version: current?.version ?? order.version,
              }))
            }
          />
        </label>
        {conflict && (
          <div role="alert" className="sg-stack">
            <p>Saved notes changed. Your draft is kept.</p>
            <div className="sg-actions">
              <button
                type="button"
                className="sg-button"
                onClick={() => setDraft(null)}
              >
                Use saved notes
              </button>
              <button
                type="button"
                className="sg-button"
                onClick={() =>
                  setDraft(
                    (current) =>
                      current && {
                        ...current,
                        base: saved,
                        version: order.version,
                      },
                  )
                }
              >
                Keep my draft
              </button>
            </div>
          </div>
        )}
        <button className="sg-button" disabled={conflict || busy}>
          Save notes
        </button>
      </form>
    </Panel>
  );
}
function Ticket({ id }: { id: number }) {
  const q = useRead<StringingDetail>(`/orders/${id}`),
    overview = useRead<StringingOverview>("/overview"),
    save = useSave(),
    key = useRequestKey(),
    paymentKey = useRequestKey();
  const [paymentVersion, setPaymentVersion] = useState<number | null>(null),
    [paymentOpen, setPaymentOpen] = useState(false),
    [paymentAmount, setPaymentAmount] = useState(""),
    [paymentBalance, setPaymentBalance] = useState(0),
    [notice, setNotice] = useState(""),
    [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (q.isError && q.data == null) {
      setPaymentOpen(false);
      setPaymentVersion(null);
    }
  }, [q.isError, q.data]);
  const order = q.data?.order,
    timezone = overview.data?.timezone;
  const activities = [...(q.data?.activity || [])].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.id - b.id,
  );
  async function update(path: string, body: Record<string, unknown>) {
    try {
      await save.mutateAsync({
        path,
        body: { ...body, requestId: body.requestId || key({ path, ...body }) },
      });
      if (path.endsWith("/payments")) setPaymentOpen(false);
      setNotice(
        path.endsWith("/payments")
          ? "Payment recorded."
          : path.endsWith("/notes")
            ? "Notes saved."
            : body.status === "STRINGING"
              ? "Stringing started."
              : body.status === "READY"
                ? "Ready for pickup."
                : body.status === "COLLECTED"
                  ? "Pickup completed."
                  : "Order cancelled.",
      );
    } catch {
      /* Retain form and retry identity. */
    }
  }
  function payment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const input = {
      version: paymentVersion ?? order!.version,
      amount: Number(f.get("amount")),
      method: f.get("method"),
      paidOn: f.get("paidOn"),
    };
    void update(`/orders/${id}/payments`, {
      ...input,
      requestId: paymentKey(input),
    });
  }
  const next = order
    ? (
        {
          RECEIVED: ["STRINGING", "Start stringing"],
          STRINGING: ["READY", "Mark ready"],
          READY: ["COLLECTED", "Complete pickup"],
        } as Record<string, string[]>
      )[order.status]
    : undefined;
  return (
    <>
      <Back to={`${base}/orders`}>Orders</Back>
      <QueryState query={q}>
        {order && (
          <>
            <Head
              title={`Order #${order.id}`}
              subtitle={`Pickup · ${when(order.dueAt, timezone)}`}
              action={<Badge status={order.status} />}
            />
            {!paymentOpen ? (
              <div className="sg-ticket-dock">
                <div className="sg-grow">
                  <strong>{order.racquet}</strong>
                  <span>
                    {order.clientName} ·{" "}
                    {order.clientMobile || `Client #${order.clientId}`}
                  </span>
                </div>
                <div className="sg-dock-balance">
                  <small>
                    {stringingBalance(order) > 0 ? "Balance" : "Paid"}
                  </small>
                  <strong>
                    {money(stringingBalance(order), order.currency)}
                  </strong>
                </div>
                <div className="sg-actions">
                  {stringingBalance(order) > 0 &&
                  order.status !== "CANCELLED" ? (
                    <button
                      className="sg-button"
                      disabled={save.isPending}
                      onClick={() => {
                        setPaymentVersion(order.version);
                        setPaymentAmount(String(stringingBalance(order)));
                        setPaymentBalance(stringingBalance(order));
                        setPaymentOpen(true);
                      }}
                    >
                      <Wallet size={17} />
                      Record payment
                    </button>
                  ) : null}
                  {next ? (
                    <button
                      className="sg-button sg-primary"
                      disabled={save.isPending}
                      onClick={() =>
                        void update(`/orders/${id}/status`, {
                          version: order.version,
                          status: next[0],
                        })
                      }
                    >
                      {save.isPending ? "Saving…" : next[1]}
                    </button>
                  ) : null}
                </div>
                {order.status === "READY" && stringingBalance(order) > 0 ? (
                  <p className="sg-caption sg-dock-note">
                    Pickup completes the handoff;{" "}
                    {money(stringingBalance(order), order.currency)} remains
                    unpaid.
                  </p>
                ) : null}
                <div className="sg-full">
                  <SaveError save={save} />
                  <p role="status" className="sg-success">
                    {notice}
                  </p>
                </div>
              </div>
            ) : (
              <Panel title="Record money received">
                <form
                  className="sg-panel-body sg-payment-editor sg-payment-form"
                  onSubmit={payment}
                >
                  <div className="sg-payment-summary">
                    <strong>
                      {order.clientName} · {order.racquet}
                    </strong>
                    <span>
                      {order.clientMobile || `Client #${order.clientId}`}
                    </span>
                    <p>
                      This records a receipt. It does not charge the client.
                    </p>
                    <span>
                      Current balance{" "}
                      <b>{money(stringingBalance(order), order.currency)}</b>
                    </span>
                  </div>
                  <label className="sg-field">
                    Amount · {order.currency}
                    <input
                      name="amount"
                      type="number"
                      required
                      value={paymentAmount}
                      onChange={(event) => setPaymentAmount(event.target.value)}
                      min="0.01"
                      step="0.01"
                    />
                  </label>
                  <label className="sg-field">
                    Method
                    <select name="method">
                      {[
                        ["CASH", "Cash"],
                        ["CHECK", "Check"],
                        ["ZELLE", "Zelle"],
                        ["VENMO", "Venmo"],
                        ["CASH_APP", "Cash App"],
                        ["OTHER_OFF_PLATFORM", "Other"],
                      ].map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Field
                    name="paidOn"
                    label="Paid on"
                    type="date"
                    required
                    value={studioDate(timezone)}
                  />
                  <p className="sg-payment-remaining">
                    Balance after receipt{" "}
                    <strong>
                      {money(
                        Math.max(
                          0,
                          paymentBalance - (Number(paymentAmount) || 0),
                        ),
                        order.currency,
                      )}
                    </strong>
                  </p>
                  <SaveError save={save} />
                  <button
                    type="button"
                    className="sg-text-button"
                    disabled={q.isFetching}
                    onClick={() => void q.refetch()}
                  >
                    Check latest balance
                  </button>
                  {save.isError ? (
                    <p className="sg-caption sg-muted">
                      Your entered receipt is kept. Check the latest balance
                      before retrying.
                    </p>
                  ) : null}
                  <div className="sg-form-actions">
                    <button
                      type="button"
                      className="sg-button"
                      disabled={save.isPending}
                      onClick={() => setPaymentOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="sg-button sg-primary"
                      disabled={save.isPending}
                    >
                      Record payment
                    </button>
                  </div>
                </form>
              </Panel>
            )}
            <div hidden={paymentOpen}>
              <ol className="sg-progress">
                {(
                  [
                    "RECEIVED",
                    "STRINGING",
                    "READY",
                    "COLLECTED",
                  ] as StringingStatus[]
                ).map((status, i, all) => (
                  <li
                    key={status}
                    className={i <= all.indexOf(order.status) ? "sg-done" : ""}
                  >
                    {stringingStatusLabel(status)}
                  </li>
                ))}
              </ol>
              <div className="sg-columns">
                <div className="sg-stack">
                  <Panel title="Racquet setup">
                    <div className="sg-panel-body">
                      <dl className="sg-specs">
                        {[
                          ["Mains", order.mainsString],
                          ["Crosses", order.crossesString],
                          [
                            "Tension · mains / crosses",
                            `${order.mainsTension} / ${order.crossesTension} ${order.tensionUnit.toLowerCase()}`,
                          ],
                          [
                            "Strings from",
                            order.playerSuppliesStrings ? "Player" : "Studio",
                          ],
                          ["Assigned to", order.assignedName || "Unassigned"],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </Panel>
                  <TicketNotes
                    order={order}
                    busy={save.isPending}
                    onSave={(version, notes) =>
                      void update(`/orders/${id}/notes`, { version, notes })
                    }
                  />
                  <Panel
                    title={`Activity · ${activities.length}`}
                    action={
                      activities.length > 2 ? (
                        <button
                          className="sg-text-button"
                          aria-expanded={historyOpen}
                          onClick={() => setHistoryOpen((value) => !value)}
                        >
                          {historyOpen ? "Recent" : "View all"}
                        </button>
                      ) : undefined
                    }
                  >
                    {(historyOpen ? activities : activities.slice(-2)).map(
                      (activity) => (
                        <div key={activity.id} className="sg-row">
                          <Check size={17} />
                          <span className="sg-grow">
                            <strong>
                              {activity.action
                                .split("_")
                                .map(
                                  (part) =>
                                    part[0] + part.slice(1).toLowerCase(),
                                )
                                .join(" ")}
                            </strong>
                            <small>
                              {activity.actorName} ·{" "}
                              {when(activity.createdAt, timezone)}
                            </small>
                          </span>
                        </div>
                      ),
                    )}
                  </Panel>
                </div>
                <div className="sg-stack">
                  <Panel title="Client">
                    <Link
                      className="sg-row"
                      state={{ stringingReturnTo: `${base}/orders/${id}` }}
                      to={`${base}/clients/${order.clientId}`}
                    >
                      <Avatar name={order.clientName} />
                      <span className="sg-grow">
                        <strong>{order.clientName}</strong>
                        <small className="sg-client-phone">
                          {order.clientMobile || `Client #${order.clientId}`}
                        </small>
                      </span>
                      <ChevronRight size={17} />
                    </Link>
                  </Panel>
                  <Panel title="Payment">
                    <div className="sg-panel-body">
                      <dl className="sg-receipt">
                        {[
                          ["Labor fee", order.laborAmount],
                          ["String materials", order.materialsAmount],
                          ["Total", stringingTotal(order)],
                          ["Received", order.paidAmount],
                          ["Balance", stringingBalance(order)],
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className={
                              label === "Balance" ? "sg-total" : undefined
                            }
                          >
                            <dt>{label}</dt>
                            <dd>{money(Number(value), order.currency)}</dd>
                          </div>
                        ))}
                      </dl>
                      {q.data?.payments.map((receipt) => (
                        <p key={receipt.id} className="sg-caption sg-muted">
                          {money(receipt.amount, receipt.currency)} ·{" "}
                          {receipt.method.split("_").join(" ")} ·{" "}
                          {receipt.paidOn}
                        </p>
                      ))}
                    </div>
                  </Panel>
                  {next ? (
                    <details className="sg-cancel">
                      <summary>Cancel order</summary>
                      <button
                        className="sg-button"
                        disabled={save.isPending}
                        onClick={() =>
                          void update(`/orders/${id}/status`, {
                            version: order.version,
                            status: "CANCELLED",
                          })
                        }
                      >
                        Confirm cancellation
                      </button>
                    </details>
                  ) : null}
                </div>
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}
function Services() {
  const { business } = useStringingScope();
  const q = useRead<StringingServiceItem[]>("/services"),
    save = useSave(),
    key = useRequestKey(),
    [editing, setEditing] = useState<StringingServiceItem | "new" | null>(null);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      item = editing !== "new" ? editing : null;
    const data = {
      name: String(f.get("name")).trim(),
      description: String(f.get("description")).trim() || null,
      laborAmount: Number(f.get("labor")),
      materialsAmount: Number(f.get("materials")),
      currency: String(f.get("currency")).trim().toUpperCase(),
      ...(item
        ? { version: item.version, active: f.get("active") === "on" }
        : {}),
    };
    try {
      await save.mutateAsync({
        path: item ? `/services/${item.id}` : "/services",
        method: item ? "PUT" : "POST",
        body: item ? data : { ...data, requestId: key(data) },
      });
      setEditing(null);
    } catch {
      /* Keep form values. */
    }
  }
  return (
    <>
      <Head
        title={
          editing
            ? editing === "new"
              ? "Add service"
              : `Edit ${editing.name}`
            : "Services"
        }
        subtitle={business}
        action={
          !editing ? (
            <button
              className="sg-button sg-primary"
              onClick={() => setEditing("new")}
            >
              <Plus size={15} />
              Add service
            </button>
          ) : (
            <button
              className="sg-button"
              disabled={save.isPending}
              onClick={() => setEditing(null)}
            >
              Back to services
            </button>
          )
        }
      />
      {!editing && (
        <QueryState query={q}>
          <div className="sg-class-grid">
            {q.data?.map((s) => (
              <section className="sg-panel sg-panel-body" key={s.id}>
                <span className="sg-badge">
                  {s.active ? "Active" : "Inactive"}
                </span>
                <h2>{s.name}</h2>
                <strong>
                  {money(
                    Number(s.laborAmount) + Number(s.materialsAmount),
                    s.currency,
                  )}
                </strong>
                <p className="sg-muted">{s.description}</p>
                <div className="sg-actions">
                  <Link
                    className="sg-button sg-primary"
                    to={`${base}/orders/new`}
                  >
                    New order
                  </Link>
                  <button className="sg-button" onClick={() => setEditing(s)}>
                    Edit
                  </button>
                </div>
              </section>
            ))}
          </div>
          {!q.data?.length && (
            <p className="sg-empty">Add your stringing services and prices.</p>
          )}
        </QueryState>
      )}
      {editing && (
        <Panel title={editing === "new" ? "Service details" : editing.name}>
          <form
            className="sg-panel-body"
            key={editing === "new" ? "new" : `${editing.id}:${editing.version}`}
            onSubmit={(e) => void submit(e)}
          >
            <div className="sg-fields">
              <Field
                name="name"
                label="Name *"
                required
                value={editing === "new" ? "" : editing.name}
                maxLength={120}
              />
              <Field
                name="description"
                label="Description"
                value={editing === "new" ? "" : editing.description || ""}
                maxLength={500}
              />
              <Field
                name="labor"
                label="Labor fee *"
                type="number"
                required
                min={0}
                step="0.01"
                value={editing === "new" ? 20 : editing.laborAmount}
              />
              <Field
                name="materials"
                label="String materials *"
                type="number"
                required
                min={0}
                step="0.01"
                value={editing === "new" ? 0 : editing.materialsAmount}
              />
              <Field
                name="currency"
                label="Currency *"
                required
                value={editing === "new" ? "USD" : editing.currency}
                maxLength={3}
              />
              {editing !== "new" && (
                <label className="sg-check">
                  <input
                    type="checkbox"
                    name="active"
                    defaultChecked={editing.active}
                  />
                  Active
                </label>
              )}
            </div>
            <SaveError save={save} />
            <div className="sg-form-actions">
              <button
                className="sg-button"
                type="button"
                disabled={save.isPending}
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
              <button
                className="sg-button sg-primary"
                disabled={save.isPending}
              >
                Save service
              </button>
            </div>
          </form>
        </Panel>
      )}
    </>
  );
}
function Inquiries() {
  const [cursor, setCursor] = useState<number | null>(null),
    q = useRead<StringingPage<StringingInquiry>>(
      `/inquiries${cursor ? `?cursor=${cursor}` : ""}`,
    );
  return (
    <>
      <Head title="Inquiries" subtitle="From your PRO listing" />
      <QueryState query={q}>
        <Panel title="Listing inquiries">
          {q.data?.items.length ? (
            q.data.items.map((r) => (
              <div className="sg-row" key={r.id}>
                <Avatar name={r.name} />
                <span className="sg-grow">
                  <strong>{r.name}</strong>
                  <small
                    className="sg-client-phone"
                    style={{ overflowWrap: "anywhere" }}
                  >
                    {r.mobile || r.email || r.contactMethod}
                  </small>
                  {r.message ? (
                    <details className="sg-client-phone">
                      <summary style={{ overflowWrap: "anywhere" }}>
                        {r.message.length > 90
                          ? `${r.message.slice(0, 90)}…`
                          : r.message}
                      </summary>
                      {r.message.length > 90 ? (
                        <p style={{ overflowWrap: "anywhere" }}>{r.message}</p>
                      ) : null}
                    </details>
                  ) : null}
                </span>
                <span className="sg-inquiry-status">
                  {r.orderId ? "Order created" : "New inquiry"}
                </span>
                <Link
                  className="sg-button"
                  to={
                    r.orderId
                      ? `${base}/orders/${r.orderId}`
                      : r.clientId
                        ? `${base}/orders/new?clientId=${r.clientId}&sourceRequestId=${r.id}${cursor ? `&sourceCursor=${cursor}` : ""}`
                        : `${base}/clients/new?userId=${r.userId}&name=${encodeURIComponent(r.name)}&sourceRequestId=${r.id}${cursor ? `&sourceCursor=${cursor}` : ""}`
                  }
                >
                  {r.orderId ? "Open order" : "Review & create order"}
                </Link>
              </div>
            ))
          ) : (
            <p className="sg-empty">No inquiries yet.</p>
          )}
        </Panel>
        <div className="sg-actions">
          {cursor && (
            <button className="sg-button" onClick={() => setCursor(null)}>
              First page
            </button>
          )}
          <More
            hasMore={q.data?.hasMore}
            onMore={() => setCursor(q.data!.nextCursor)}
          />
        </div>
      </QueryState>
    </>
  );
}
